import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import PolicyStore from '../../../stores/Policy/PolicyStore';
import Accordion from '../../Accordion/Accordion';
import { FuseConsoleCardFooter } from '../../ConsoleFragments';
import ConsoleUL from '../../ConsoleUL/ConsoleUL';
import { Card, FTBody, FTHeading, Page } from '../../Fuse';
import ConsoleStore from '../../../stores/ConsoleStore';
import StackL from '../../Spacers/Spacers';
import './Overview.css';

const block = b.lock('Overview');

class Overview extends Component {
    static propTypes = {
        console: ConsoleStore.PropType.isRequired,
        policy: PolicyStore.PropType.isRequired,
    };

    renderFtrWhatAreSection(shouldHideDLP) {
        return shouldHideDLP ? <li>
            File Threat Rules can provide an Isolated preview for blocked file downloads in Isolation.
            (TAP URL Isolation license does not support uploads or downloads.)
        </li> : <Fragment>
            <li>
                File Threat Rules can allow, block, and prevent malware in file uploads/downloads
                in Isolation. These rules can also be configured to offer file previews even when
                downloads are blocked.
            </li>
            <li>
                File DLP Rules can allow, block, and prevent data loss in files which are allowed in Isolation.
            </li>
        </Fragment>;
    }

    renderFtrPrioritization(shouldHideDLP) {
        return shouldHideDLP ? <Fragment>
            If a Content Rule does not block the site and the requested URL is a file,
            the system evaluates File Threat Rules.
        </Fragment> : <Fragment >
            If a Content Rule does not block the site and the requested URL is a file,
            the system evaluates File Threat Rules. If no File Threat Rule blocks the file,
            the system evaluates your File DLP Rules.
        </Fragment>;
    }

    renderFtrEditSection(shouldHideDLP, pProps) {
        return shouldHideDLP ? <FTBody {...pProps}>
            Edit the <b>Default File Threat Rule</b> to establish the baseline file preview permissions
            for blocked downloads in Isolation.
        </FTBody> : <Fragment>
            <FTBody {...pProps}>
                Edit the <b>Default File Threat Rule</b> to establish the baseline permissions for
                file uploads/downloads in Isolation.
            </FTBody>
            <FTBody {...pProps}>
                Edit the <b>Default File DLP Rule</b> to establish the baseline DLP settings for
                file uploads/downloads in Isolation.
            </FTBody>
        </Fragment>;
    }

    renderFtrCreateRuleSection(shouldHideDLP, pProps) {
        return shouldHideDLP ? <Fragment>
            <FTBody {...pProps}>
                Add <b>File Threat Rules</b> to define policies for blocked file
                download requests in Isolation.
            </FTBody>
            <FTBody>
                Example 1: Provide an Isolated preview of blocked Microsoft 365 documents in Isolation.
            </FTBody>
        </Fragment> : <Fragment>
            <FTBody {...pProps}>
                Add <b>File Threat Rules</b> to define policies for file operation requests
                in Isolation (File uploads and downloads).
            </FTBody>
            <FTBody>
                Example 1: Allow only Microsoft 365 documents to be downloaded
                if they do not contain any malware.
            </FTBody>
            <FTBody {...pProps}>
                Example 2: Prevent all uploads and downloads for Uncategorized websites.
            </FTBody>
            <FTBody {...pProps}>
                <b>File DLP Rules</b> are only applied if your File Threat Rules do not block the file.
                File DLP Rules can apply file upload/download data loss policies by matching
                with your organization’s DLP detectors.
            </FTBody>
            <FTBody {...pProps}>
                Example: Block all users from uploading and downloading any
                files that contain Social Security Numbers.
            </FTBody>
            <FTBody {...pProps}>
                <b>NOTE:</b> Added File DLP Rules can match with your existing DLP detector sets,
                which are managed in Proofpoint’s dedicated DLP application.
                Make sure your detectors and detector sets are defined in the DLP app before
                creating File DLP Rules in the Isolation app that will scan files for data loss
                violations within Isolation.
            </FTBody>
        </Fragment>;
    }

    render() {
        const store = this.props.policy.overview;
        const {
            urlIsolationProductEnabled,
            useFileThreatRules,
        } = this.props.console;
        const shouldHideDLP = !(this.props.console.hasBrowserOrEmailLicense() && useFileThreatRules);

        const accProps = (n) => {
            const flagStore = store[`step${n}`];
            return {
                flagStore,
                labelClassName: block('Accordion', { expanded: flagStore.getValue() }),
                iconClassName: block('AccordionIcon'),
                qa: `step-${n}-trigger`
            };
        };
        const pProps = { className: block('Paragraph') };
        const footerText = useFileThreatRules ?
            'If no Rules are added, the system enforces Default Rules for all URLs in Isolation.' :
            'If no Content Rules are added, the system enforces the Default Content Rule for all URLs in Isolation.';

        return <Page title="Policies - Overview">
            <Card>
                <FTHeading>What Are Policies?</FTHeading>
                <StackL />
                <FTBody>
                    When a user requests a URL, the system enforces a new Policy based on the URL
                    request’s detected attributes. An applied Policy can enforce a single Role and/or Rule.
                </FTBody>
                <StackL />
                <FTBody>Create Isolation Rules for the following:</FTBody>
                <ConsoleUL>
                    <li>
                        Exclusion Rules direct specific sites to open in the user’s native browser,
                        i.e., not in the Isolation Browser.
                    </li>
                    {urlIsolationProductEnabled ? <li>
                        Redirect Rules send specific rewritten TAP URL Defense links
                        to Isolation (URL Isolation only).
                    </li> : null}
                    <li>
                        Content Rules either block or allow page content in Isolation.
                    </li>
                    {useFileThreatRules ? this.renderFtrWhatAreSection(shouldHideDLP) : null}
                </ConsoleUL>
                <StackL />
                <FTBody>
                    Most match criteria items are system generated. The following
                    match criteria items can be created by your organization:
                </FTBody>
                <ConsoleUL>
                    <li>
                        Added Browsing Roles are specific sets of users provided with unique Isolation options.
                    </li>
                    <li>
                        Added Custom Categories are specific sets of URLs.
                    </li>
                    {!shouldHideDLP ? <li>
                        Detector Sets in your dedicated Data Loss Prevention application are
                        specified ways to identify sensitive information.
                    </li> : null}
                </ConsoleUL>
                <FTHeading> Rule Prioritization </FTHeading>
                <StackL />
                <FTBody>Only the highest prioritized Rule that matches all detected attributes is applied.</FTBody>
                <StackL />
                <FTBody>
                    The system evaluates rules in the following order: Exclusion Rules, Redirect Rules,
                    Content Rules. {useFileThreatRules ? this.renderFtrPrioritization(shouldHideDLP) : null}
                </FTBody>
                <StackL />
                <FTHeading> How to Administer Policies </FTHeading>
                <StackL />
                <Accordion {...accProps(1)} label={<div>Step 1: Edit All Default Settings</div>}>
                    <FTBody {...pProps}>
                        Edit the <b>Default Browsing Role</b> to establish the baseline registration settings
                        and additional default options for all users in Isolation.
                    </FTBody>
                    <FTBody {...pProps}>
                        Edit the <b>Default Content Rule</b> to establish the baseline content and default
                        security settings for all pages in Isolation.
                    </FTBody>
                    {useFileThreatRules ? this.renderFtrEditSection(shouldHideDLP, pProps) : null}
                    <FTBody {...pProps}>
                        To create exceptions to your baseline settings,
                        you will add new Roles and Rules that reference specific sites and/or users.
                    </FTBody>
                </Accordion>

                <Accordion {...accProps(2)} label={<div>Step 2: Add Browsing Roles</div>}>
                    <FTBody {...pProps}>
                        In <b>Browsing Roles</b>, first select the User Group Source (the user group directory
                        that you will synchronize into the Console.) This step specifies the source of the user
                        group information that you can use to define added Browsing Role.
                    </FTBody>
                    <FTBody {...pProps}>
                        Add <b>Browsing Roles </b>
                        to specific unique user sets with unique Isolation options. Added roles can be referenced
                        in policy rules to create controls for targeted groups of users.
                    </FTBody>
                    <FTBody {...pProps}>
                        Example: A Browsing Role for Threat Researchers.
                    </FTBody>
                </Accordion>

                <Accordion {...accProps(3)} label={<div>Step 3: Add Custom Categories</div>}>
                    <FTBody {...pProps}>
                        In <b>Custom Categories</b>, add custom categories to represent lists of URLs or IP addresses.
                        Added custom categories can be referenced in policy rules to create controls for targeted sites.
                    </FTBody>

                    <FTBody {...pProps}>
                        Example: A Custom Category for “Never Isolate URLs” (https://zoom.us/).
                    </FTBody>
                </Accordion>

                <Accordion {...accProps(4)}
                    contentClassName={block('LastAccordionContent')}
                    label={<div>Step 4: Create Rules to Provide Exceptions</div>}
                >
                    <FTBody {...pProps}>
                        Now you can create rules that layer on permissions/restrictions for specific users
                        and sites by using the <b>Browsing Roles</b> and <b>Custom Categories</b> you
                        created in Steps 2 and 3.
                    </FTBody>
                    <FTBody {...pProps}>
                        Add <b>Exclusion Rules</b> to send matching URLs to open on the user’s native browser.
                    </FTBody>
                    <FTBody {...pProps}>
                        Example: Never Isolate URLs.
                    </FTBody>
                    {urlIsolationProductEnabled ? <Fragment>
                        <FTBody {...pProps}>
                            Add <b>Redirect Rules</b> to send matching TAP URL Defense links to open
                            Isolation (URL Isolation Only).
                        </FTBody>
                        <FTBody {...pProps}>
                            Example: Redirect VAPs.
                        </FTBody>
                    </Fragment> : null}
                    <FTBody {...pProps}>
                        Add <b>Content Rules</b> to enforce unique content and security permissions on pages in
                        Isolation. (Isolated page content only.)
                    </FTBody>
                    <FTBody {...pProps}>
                        Example: Block Sites that Violate HR Policy.
                    </FTBody>
                    {useFileThreatRules ? this.renderFtrCreateRuleSection(shouldHideDLP, pProps) : null}
                </Accordion>
                <StackL />
                <FuseConsoleCardFooter text={footerText}/>
            </Card>
        </Page>;
    }
}

export default inject('console', 'policy')(observer(Overview));
